// TODO: Overhaul — delete this file once the consumer managed-funds/valuations endpoint is reachable. Interim,
// it serves the overhaul path `…/managed-funds/valuations` from the live legacy API1 endpoint
// `/v2/investor-entities/{investorEntityId}/managed-funds/valuations`.
import { Client } from 'openapi-fetch';

import { mockDelay, ok, randomLatencyMs } from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreContractsCreateManagedFundRedemptionForm as CreateManagedFundRedemptionForm,
  SquirrelMoneyCoreContractsInvestmentPlanPositionDto as InvestmentPlanPositionDto,
  SquirrelMoneyCoreContractsInvestorEntityDto as InvestorEntityDto,
  SquirrelMoneyCoreContractsInvestorEntityManagedFundsDto as InvestorEntityManagedFundsDto,
  SquirrelMoneyCoreContractsManagedFundDto as ManagedFundDto,
  SquirrelMoneyCoreContractsManagedFundRedemptionDto as ManagedFundRedemptionDto,
  SquirrelMoneyCoreContractsUpdateInvestmentPlanForm as UpdateInvestmentPlanForm,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { paths as api1Paths } from '@core/schemas/api1-schema';

interface GetInit {
  params?: { path?: { investorEntityId?: number } };
}
interface PutInit {
  params?: { path?: { investorEntityId?: number } };
  body?: UpdateInvestmentPlanForm;
}
interface PostInit {
  body?: CreateManagedFundRedemptionForm;
}

// TODO: Overhaul — the legacy v2 valuations endpoint carries no wholesale investment plan, so we layer a
// representative one in until the real overhaul endpoint lands. Four plan-eligible wholesale funds; the plan
// allocates two of them and leaves two unallocated, so the allocation list exercises the 0% rows the real screen
// shows (a plan carries a position only for the funds it allocates).
const MOCK_AVAILABLE_FUNDS: ManagedFundDto[] = [
  { managedFundId: 1, name: 'Wholesale Home Loan Fund', isWholesaleOnly: true, useInvestmentPlan: true },
  { managedFundId: 2, name: 'Wholesale Construction Loan Fund', isWholesaleOnly: true, useInvestmentPlan: true },
  { managedFundId: 3, name: 'Specialised Income Fund', isWholesaleOnly: true, useInvestmentPlan: true },
  { managedFundId: 4, name: 'Monthly Income Fund', isWholesaleOnly: true, useInvestmentPlan: true },
];
const MOCK_INVESTMENT_PLAN: InvestmentPlanPositionDto[] = [
  { managedFundId: 1, percent: 60 },
  { managedFundId: 2, percent: 40 },
  // funds 3 and 4 carry no position → they render at 0%
];

/**
 * Mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving managed-fund valuations from the live legacy endpoint. A saved
 * investment plan is held in memory per entity (the mocked funds have no real ids to save against) and layered
 * over the live plan on the next read.
 */
export function createManagedFundMockClient(deps: { api1: Client<api1Paths> }): Client<accountOverhaulPaths> {
  const planByEntity = new Map<number, InvestmentPlanPositionDto[]>();

  return {
    async GET(_path: string, init?: GetInit) {
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return ok(null);
      const { data, error, response } = await deps.api1.GET(
        '/v2/investor-entities/{investorEntityId}/managed-funds/valuations',
        { params: { path: { investorEntityId: entityId } } },
      );
      if (error) return { data: undefined, error, response };
      // Layer the mocked investment plan onto the legacy payload (only when it carries none), so the
      // move-and-invest allocation step and review "Allocated as:" list have data to render.
      const legacy = data ?? {};
      const augmented: InvestorEntityManagedFundsDto = {
        ...legacy,
        investmentPlan: planByEntity.get(entityId) ?? legacy.investmentPlan ?? MOCK_INVESTMENT_PLAN,
        availableManagedFunds: legacy.availableManagedFunds ?? MOCK_AVAILABLE_FUNDS,
      };
      return ok(augmented);
    },
    async PUT(path: string, init?: PutInit) {
      const entityId = init?.params?.path?.investorEntityId;
      if (!path.endsWith('/managed-funds/settings/investment-plan') || entityId === undefined) {
        throw new Error(`managed-fund mock: unhandled PUT ${path}`);
      }
      await mockDelay(randomLatencyMs());
      const investmentPlan = init?.body?.investmentPlan ?? [];
      planByEntity.set(entityId, investmentPlan);
      return ok<InvestorEntityDto>({ investorEntityId: entityId, investmentPlan });
    },
    // Accepts the redemption without touching the holdings — the legacy valuations stay the source of truth.
    async POST(path: string, init?: PostInit) {
      if (!path.endsWith('/redemptions')) throw new Error(`managed-fund mock: unhandled POST ${path}`);
      await mockDelay(randomLatencyMs());
      const form = init?.body;
      return ok<ManagedFundRedemptionDto>({
        investorEntityId: form?.investorEntityId,
        investmentAccountId: form?.investmentAccountId,
        managedFundId: form?.managedFundId,
        redemptionType: form?.redemptionType,
        amount: form?.amount ?? undefined,
        units: form?.units ?? undefined,
        managedFundRedemptionId: Date.now(),
        createDateNzt: new Date().toISOString(),
        isConfirmedProcessing: false,
        isConfirmedComplete: false,
        isConfirmedCancelled: false,
      });
    },
    // Only the valuations GET, the investment-plan PUT and the redemption POST are implemented, not the whole
    // overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
